import type { ComponentProps } from "react";
import { useMemo } from "react";

import {
  useGetEncounterById,
  useListActionItemsForEncounter,
  useListAgendaItems,
} from "@elation/api-client/src/stitch-web";

import { ActionItemEntity } from "@src/features/action-items";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Spinner } from "@src/shared/components/ui/spinner";

import styles from "./ActionItems.module.less";
import type { ActionItemSection } from "./ActionItems.types";
import { ActionItemsSection } from "./ActionItemsSection";

type ActionItemsSectionActionItems = ComponentProps<
  typeof ActionItemsSection
>["actionItems"];

export interface ActionItemsProps {
  encounterId: number;
}

export const ActionItems = ({ encounterId }: ActionItemsProps) => {
  const enabled = Number.isFinite(encounterId) && encounterId > 0;

  const { isLoading: isLoadingEncounter } = useGetEncounterById(encounterId, {
    query: { enabled },
  });

  const { isLoading: isLoadingAgendaItems } = useListAgendaItems(encounterId, {
    query: { enabled },
  });

  const { data: actionItemsResponse, isLoading: isLoadingActionItems } =
    useListActionItemsForEncounter(encounterId, {
      query: { enabled },
    });

  const actionItemsByCategory = useMemo((): Record<
    ActionItemSection,
    ActionItemsSectionActionItems
  > => {
    const rows = actionItemsResponse?.data.items ?? [];
    const grouped = ActionItemEntity.groupBySection(rows);

    return {
      Prescriptions: grouped.Prescriptions.map((item) => ({
        ...item,
        agendaItem: null,
      })),
      Orders: grouped.Orders.map((item) => ({
        ...item,
        agendaItem: null,
      })),
      Referrals: grouped.Referrals.map((item) => ({
        ...item,
        agendaItem: null,
      })),
      Miscellaneous: grouped.Miscellaneous.map((item) => ({
        ...item,
        agendaItem: null,
      })),
      Assessments: grouped.Assessments.map((item) => ({
        ...item,
        agendaItem: null,
      })),
      "Patient Education": grouped["Patient Education"].map((item) => ({
        ...item,
        agendaItem: null,
      })),
    };
  }, [actionItemsResponse?.data.items]);

  const isLoading =
    isLoadingEncounter ||
    (enabled && isLoadingAgendaItems) ||
    (enabled && isLoadingActionItems);

  if (!enabled) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        className="rounded-lg bg-muted/40 p-3 text-center"
      >
        <Text appearance="system">Invalid encounter.</Text>
      </HorizontalFlexbox>
    );
  }

  if (isLoading) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        className="rounded-lg bg-muted/40 p-3"
      >
        <Spinner />
      </HorizontalFlexbox>
    );
  }

  const totalCount = Object.values(actionItemsByCategory).reduce(
    (n, items) => n + items.length,
    0,
  );

  if (totalCount === 0) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        className="rounded-lg bg-muted/40 p-3 text-center"
      >
        <Text appearance="system">
          No action items from completed agenda items
        </Text>
      </HorizontalFlexbox>
    );
  }

  return (
    <VerticalFlexbox
      className={`${styles.root} lg-stitch:min-h-0 lg-stitch:flex-1 lg-stitch:overflow-auto`}
    >
      <VerticalFlexbox as="ul" className={styles.actionItems}>
        {Object.entries(actionItemsByCategory).map(([category, items]) =>
          items.length > 0 ? (
            <ActionItemsSection
              key={category}
              section={category as ActionItemSection}
              actionItems={items}
              encounterId={encounterId}
            />
          ) : null,
        )}
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
};
